import { ArrowRight, Check, Eye, GitPullRequest, ShieldCheck } from "lucide-react"
import type { Metadata } from "next"
import Link from "next/link"
import { ProductPreview } from "@/components/product-preview"

export const metadata: Metadata = {
  title: "Product",
  description:
    "See how Resolto turns internal support requests into evidence-backed explanations, governed actions, and complete escalations.",
}

export default function ProductPage() {
  return (
    <main id="main-content">
      <section className="marketing-page-hero">
        <div className="marketing-container">
          <p className="section-kicker">Product</p>
          <h1>The support workspace between a vague request and a safe resolution.</h1>
          <p>
            Resolto gives employees a simple place to report issues and gives developers a governed
            system for data access, playbooks, approvals, evidence, and code review.
          </p>
          <div className="button-row">
            <Link className="button button-primary button-prominent" href="/contact">
              Book a design-partner call
            </Link>
            <Link className="button button-secondary button-prominent" href="/app">
              Open demo workspace
            </Link>
          </div>
        </div>
      </section>
      <section className="marketing-container product-page-preview">
        <ProductPreview />
      </section>
      <section className="marketing-section">
        <div className="marketing-container capability-bands">
          <article>
            <span className="capability-level">Level 1</span>
            <Eye aria-hidden="true" />
            <h2>Investigate</h2>
            <p>
              Search approved knowledge, records, events, and logs. Show the exact evidence,
              confidence, contradictions, and missing data.
            </p>
            <ul>
              <li>
                <Check aria-hidden="true" />
                Read-only by default
              </li>
              <li>
                <Check aria-hidden="true" />
                Typed, allowlisted connector operations
              </li>
              <li>
                <Check aria-hidden="true" />
                Complete developer escalation
              </li>
            </ul>
          </article>
          <article>
            <span className="capability-level">Level 2</span>
            <ShieldCheck aria-hidden="true" />
            <h2>Approve and resolve</h2>
            <p>
              Present the exact target, current state, proposed change, required authority, and
              verification before a deterministic action can run.
            </p>
            <ul>
              <li>
                <Check aria-hidden="true" />
                Named approvers and recent-auth rules
              </li>
              <li>
                <Check aria-hidden="true" />
                Idempotent execution and postconditions
              </li>
              <li>
                <Check aria-hidden="true" />
                Rollback metadata where supported
              </li>
            </ul>
          </article>
          <article>
            <span className="capability-level">Level 3</span>
            <GitPullRequest aria-hidden="true" />
            <h2>Prepare a code proposal</h2>
            <p>
              Reproduce an eligible recurring defect inside a bounded sandbox, add tests, and
              prepare a pull request for developer review.
            </p>
            <ul>
              <li>
                <Check aria-hidden="true" />
                Developer approval before generation
              </li>
              <li>
                <Check aria-hidden="true" />
                Scoped repository paths and command allowlist
              </li>
              <li>
                <Check aria-hidden="true" />
                Never merges or deploys
              </li>
            </ul>
          </article>
        </div>
      </section>
      <section className="marketing-section compact-cta-section">
        <div className="marketing-container compact-cta">
          <div>
            <h2>See the full evidence path in the working demo.</h2>
            <p>Explore seeded, visibly labelled data without connecting an external account.</p>
          </div>
          <Link className="button button-primary" href="/app/cases/case_payment_entitlement">
            Open example investigation
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  )
}
